import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getAllShipments,
    createShipment,
    deleteShipment,
} from "../api/shipmentService";

import PageHeader from "./ui/PageHeader";
import InfoPanel from "./ui/InfoPanel";
import StatusBadge from "./ui/StatusBadge";
import DataTable from "./ui/DataTable";
import {
    PrimaryButton,
    SuccessButton,
    DangerButton,
    SecondaryButton,
} from "./ui/FormControls";

const Shipment = () => {
    const navigate = useNavigate();

    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionId, setActionId] = useState(null);
    const [error, setError] = useState(null);

    const fetchShipments = async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await getAllShipments();
            setShipments(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err.response || err);
            setError("Failed to fetch shipments.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShipments();

        const interval = setInterval(fetchShipments, 5000);
        return () => clearInterval(interval);
    }, []);

    const handleCreateShipment = async () => {
        setLoading(true);
        setError(null);

        try {
            const createdShipment = await createShipment({
                paymentStatus: 0,
            });

            if (createdShipment?.shipment) {
                setShipments((prev) => [createdShipment.shipment, ...prev]);
            } else {
                await fetchShipments();
            }
        } catch (err) {
            console.error(err.response || err);
            setError("Failed to create shipment.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteShipment = async (id) => {
        if (!window.confirm("Cancel this shipment?")) return;

        setActionId(id);
        setError(null);

        try {
            await deleteShipment(id);
            setShipments((prev) => prev.filter((sh) => sh.id !== id));
        } catch (err) {
            console.error(err.response || err);
            setError("Failed to delete shipment.");
        } finally {
            setActionId(null);
        }
    };

    const safe = (value) =>
        value === null || value === undefined || value === "" ? "—" : value;

    const stats = useMemo(() => {
        const total = shipments.length;
        const paid = shipments.filter(
            (x) =>
                String(x.paymentStatus).toLowerCase() === "completed" ||
                String(x.paymentStatus).toLowerCase() === "paid"
        ).length;

        return {
            total,
            paid,
            unpaid: total - paid,
        };
    }, [shipments]);

    const columns = [
        {
            key: "trackingNumber",
            label: "Tracking",
            render: (row) => (
                <span className="font-semibold">
                    {safe(row.trackingNumber)}
                </span>
            ),
        },
        {
            key: "status",
            label: "Status",
            render: (row) => <StatusBadge status={safe(row.status)} />,
        },
        {
            key: "paymentStatus",
            label: "Payment",
            render: (row) => {
                const paid =
                    String(row.paymentStatus).toLowerCase() === "completed" ||
                    String(row.paymentStatus).toLowerCase() === "paid";

                return (
                    <StatusBadge status={paid ? "Paid" : "Not paid"} />
                );
            },
        },
        {
            key: "createdAt",
            label: "Created",
            render: (row) =>
                row.createdAt
                    ? new Date(row.createdAt).toLocaleString()
                    : "—",
        },
        {
            key: "actions",
            label: "Actions",
            render: (row) => {
                const paid =
                    String(row.paymentStatus).toLowerCase() === "completed" ||
                    String(row.paymentStatus).toLowerCase() === "paid";

                return (
                    <div className="flex flex-wrap gap-2">
                        {!paid && (
                            <SuccessButton
                                className="px-3 py-2"
                                disabled={actionId === row.id}
                                onClick={() =>
                                    navigate(`/payment?shipmentId=${row.id}`)
                                }
                            >
                                Pay
                            </SuccessButton>
                        )}

                        <DangerButton
                            className="px-3 py-2"
                            disabled={actionId === row.id}
                            onClick={() => handleDeleteShipment(row.id)}
                        >
                            {actionId === row.id ? "Deleting..." : "Cancel"}
                        </DangerButton>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Shipments"
                subtitle="Create, monitor and manage shipment records."
                actions={
                    <div className="flex flex-wrap gap-3">
                        <SecondaryButton onClick={fetchShipments}>
                            Refresh
                        </SecondaryButton>

                        <PrimaryButton
                            onClick={handleCreateShipment}
                            disabled={loading && !actionId}
                        >
                            {loading && !actionId
                                ? "Creating..."
                                : "Create Shipment"}
                        </PrimaryButton>
                    </div>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <InfoPanel title="Total Shipments">
                    <div className="text-4xl font-black text-blue-700">
                        {stats.total}
                    </div>
                </InfoPanel>

                <InfoPanel title="Paid">
                    <div className="text-4xl font-black text-green-700">
                        {stats.paid}
                    </div>
                </InfoPanel>

                <InfoPanel title="Not Paid">
                    <div className="text-4xl font-black text-red-700">
                        {stats.unpaid}
                    </div>
                </InfoPanel>
            </div>

            <InfoPanel title="Shipment Registry">
                {loading && !actionId && (
                    <p className="text-slate-500 mb-4">
                        Loading shipments...
                    </p>
                )}

                <DataTable
                    columns={columns}
                    rows={shipments}
                    rowKey="id"
                    emptyText="No shipments found."
                />
            </InfoPanel>
        </div>
    );
};

export default Shipment;